import { Box, Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import Button from 'components/ui/Button';
import TimeLine from 'features/clinical-history/components/TimeLine';
import React, { useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { ClinicalEpisode, EstadoEpisodio } from 'types/models';

// ─── Mock data ────────────────────────────────────────────────────────────────

function mockEp(overrides: Partial<ClinicalEpisode> = {}): ClinicalEpisode {
  return {
    id:                     'ep-1',
    tarjeteroId:            'tarj-1',
    codigoHc:               'HC-2024-001',
    pacienteId:             'pac-preview',
    profesionalId:          'prof-1',
    estado:                 EstadoEpisodio.ABIERTO,
    motivoConsulta:         'Dolor lumbar de 3 semanas de evolución, intensidad EVA 7/10.',
    fechaApertura:          '2026-05-01',
    fechaCierre:            null,
    diagnosticoPrincipal:   null,
    codigoCie10:            null,
    diagnosticoSecundario:  null,
    diagnosticoDiferencial: null,
    notaApertura:           null,
    notaCierre:             null,
    anamnesis:              null,
    signosVitales:          null,
    pruebasSolicitadas:     null,
    appointmentId:          null,
    createdAt:              '2026-05-01T09:00:00Z',
    updatedAt:              '2026-05-01T09:00:00Z',
    ...overrides,
  };
}

const ACTIVE_EPISODES: ClinicalEpisode[] = [
  mockEp({
    id:           'ep-active-1',
    codigoHc:     'HC-2026-007',
    estado:       EstadoEpisodio.EN_TRATAMIENTO,
    motivoConsulta: 'Cervicalgia postraumática tras accidente de tráfico. Contractura bilateral C4-C6.',
    diagnosticoPrincipal: 'Cervicalgia mecánica postraumática con contractura muscular paravertebral',
    codigoCie10:  'M54.2',
    fechaApertura: '2026-04-18',
  }),
  mockEp({
    id:           'ep-active-2',
    codigoHc:     'HC-2026-012',
    estado:       EstadoEpisodio.ABIERTO,
    motivoConsulta: 'Gonalgia medial derecha de inicio insidioso, sin traumatismo previo.',
    fechaApertura: '2026-06-01',
  }),
];

const PAST_EPISODES: ClinicalEpisode[] = [
  mockEp({
    id:                   'ep-past-1',
    codigoHc:             'HC-2025-089',
    estado:               EstadoEpisodio.CERRADO,
    motivoConsulta:       'Tendinitis del manguito rotador hombro derecho, EVA 6/10.',
    diagnosticoPrincipal: 'Tendinitis del supraespinoso grado II',
    codigoCie10:          'M75.1',
    fechaApertura:        '2025-08-10',
    fechaCierre:          '2025-10-22',
  }),
  mockEp({
    id:                   'ep-past-2',
    codigoHc:             'HC-2025-033',
    estado:               EstadoEpisodio.CERRADO,
    motivoConsulta:       'Esguince de tobillo grado II tras caída en escalera.',
    diagnosticoPrincipal: 'Esguince ligamento lateral externo grado II',
    codigoCie10:          'S93.4',
    fechaApertura:        '2025-03-14',
    fechaCierre:          '2025-05-02',
  }),
  mockEp({
    id:             'ep-past-3',
    codigoHc:       'HC-2024-011',
    estado:         EstadoEpisodio.ARCHIVADO,
    motivoConsulta: 'Gonartrosis bilateral grado II-III, dolor crónico.',
    fechaApertura:  '2024-03-01',
    fechaCierre:    '2024-07-14',
  }),
];

const ALL_EPISODES = [...ACTIVE_EPISODES, ...PAST_EPISODES];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TimeLinePreview() {
  const bg         = useColorModeValue('gray.50', 'navy.900');
  const panelBg    = useColorModeValue('white', 'navy.800');
  const panelBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const titleColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const dotActive  = '#F6AD55';
  const dotPast    = '#A0AEC0';
  const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  const [expansionOverrides, setExpansionOverrides] = useState<Map<string, boolean>>(new Map());

  const isExpanded = (ep: ClinicalEpisode): boolean => {
    const defaultExpanded =
      ep.estado === EstadoEpisodio.ABIERTO ||
      ep.estado === EstadoEpisodio.EN_TRATAMIENTO;
    return expansionOverrides.has(ep.id)
      ? expansionOverrides.get(ep.id)!
      : defaultExpanded;
  };

  const toggleExpand = (ep: ClinicalEpisode) => {
    setExpansionOverrides(prev => {
      const next = new Map(prev);
      next.set(ep.id, !isExpanded(ep));
      return next;
    });
  };

  const setAllPastExpanded = (expanded: boolean) => {
    setExpansionOverrides(prev => {
      const next = new Map(prev);
      PAST_EPISODES.forEach(ep => next.set(ep.id, expanded));
      return next;
    });
  };

  return (
    <Box minH='100vh' bg={bg} px={{ base: '24px', md: '48px', lg: '80px' }} py='52px'>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box mb='40px'>
        <Text fontSize='2xl' fontWeight='800' color={titleColor} mb='4px'>
          TimeLine — Preview
        </Text>
        <Text fontSize='sm' color={mutedColor}>
          Historia clínica · episodios colapsables · dot + spine pattern
        </Text>
      </Box>

      {/* ── Panel simulando PatientTabs ───────────────────────────────────── */}
      <Box
        bg={panelBg}
        border='1px solid'
        borderColor={panelBorder}
        borderRadius='20px'
        p='20px'
        maxW='860px'>

        {/* Stats + action bar */}
        <Flex align='center' justify='space-between' mb='16px'>
          <Text fontSize='xs' color={mutedColor} fontWeight='600'>
            {ALL_EPISODES.length} episodios
            <Box as='span' color='orange.400' ml='6px'>
              · {ACTIVE_EPISODES.length} activos
            </Box>
          </Text>
          <Button size='sm' leftIcon={<Icon as={MdAdd} />} isDisabled>
            Nuevo Episodio
          </Button>
        </Flex>

        {/* Scrollable area */}
        <Box
          maxH='calc(100vh - 280px)'
          overflowY='auto'
          overflowX='hidden'
          pr='2px'
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { bg: 'transparent' },
            '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: '4px' },
          }}>

          {/* Episodios Activos */}
          <SectionDivider
            label={`Episodios Activos · ${ACTIVE_EPISODES.length}`}
            dotColor={dotActive}
            mutedColor={mutedColor}
            dividerColor={dividerColor}
          />
          <TimeLine
            episodes={ACTIVE_EPISODES}
            isExpanded={isExpanded}
            toggleExpand={toggleExpand}
            canWrite
            isAdmin={false}
          />

          {/* Historial */}
          <Box mt='24px'>
            <SectionDivider
              label={`Historial · ${PAST_EPISODES.length}`}
              dotColor={dotPast}
              mutedColor={mutedColor}
              dividerColor={dividerColor}>
              <Flex gap='4px'>
                <Button
                  size='xs'
                  variant='ghost'
                  fontSize='10px'
                  color={mutedColor}
                  onClick={() => setAllPastExpanded(true)}>
                  Expandir todo
                </Button>
                <Button
                  size='xs'
                  variant='ghost'
                  fontSize='10px'
                  color={mutedColor}
                  onClick={() => setAllPastExpanded(false)}>
                  Colapsar todo
                </Button>
              </Flex>
            </SectionDivider>
            <TimeLine
              episodes={PAST_EPISODES}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              canWrite={false}
              isAdmin={false}
            />
          </Box>
        </Box>
      </Box>

      <Text fontSize='xs' color={mutedColor} mt='24px'>
        API calls retornan vacío — preview usa IDs mock. En producción usar dentro de HistoriaClinicaTab.
      </Text>
    </Box>
  );
}

// ─── Section divider ──────────────────────────────────────────────────────────

function SectionDivider({
  label,
  dotColor,
  mutedColor,
  dividerColor,
  children,
}: {
  label: string;
  dotColor: string;
  mutedColor: string;
  dividerColor: string;
  children?: React.ReactNode;
}) {
  return (
    <Flex align='center' gap='8px' mb='14px'>
      <Box w='8px' h='8px' borderRadius='full' bg={dotColor} flexShrink={0} />
      <Text
        fontSize='10px'
        fontWeight='800'
        color={mutedColor}
        textTransform='uppercase'
        letterSpacing='0.1em'
        flexShrink={0}>
        {label}
      </Text>
      <Box flex={1} h='1px' bg={dividerColor} />
      {children}
    </Flex>
  );
}
